import { produce, Draft } from 'immer'
import { TActionTypes, IAction } from '@Root/types'
import without from 'lodash/without'
import filter from 'lodash/filter'
import map from 'lodash/map'
import find from 'lodash/find'

import {
  ProjectActionTypes,
  IProjectState,
  TProjectActionTypes,
  IFetchProjectsSuccessResponseBody,
  IUpdateProjectsSuccessResponseBody,
  IChangeSelectedProjectsPayload,
  IDeleteProjectsSuccessPayload,
  ISetSearchTextPayload,
  IncludedResource,
  IProject,
  IProjectData,
  IUpdateProjectTitleSuccessPayload,
} from './types'

import {
  WorkspaceActionTypes
} from '@SiteModules/Workspace/types'

import {
  ProjectVersionSettingsActionTypes,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  LabelActionTypes,
} from '@SiteModules/Label/types'

import {
  ProjectVersionActionTypes,
  IProjectVersionData,
  IProjectVersion,
  TProjectVersionActionTypes,
  IDeleteProjectVersionsSuccessPayload,
  IUpdateProjectVersionTitleSuccessPayload,
} from '@SiteModules/ProjectVersion/types'

import {
  SubtitleActionTypes,
  TSubtitleActionTypes,
  IDeleteLanguageSuccessPayload,
  ISubtitleData,
  ISubtitle,
} from '@SiteModules/Subtitle/types'

export const initialState: IProjectState = {
  projects: [],
  hasLoadedInitially: false,
  loading: false,
  deleting: false,
  updating: false,
  startingBulkExportation: false,
  selectedProjectIds: [],
  searchText: '',
  page: {
    currentPage: 0,
    totalCount: 0,
    totalPages: 1,
    shouldCheckAutoFillGrid: false,
    shouldCheckAutoFillList: false,
    listActive: false
  }
}

type TAction = TActionTypes | TProjectActionTypes | TSubtitleActionTypes | TProjectVersionActionTypes

export const projectsReducer = (
  state: IProjectState = initialState,
  action: TAction
): IProjectState => {
  return produce(state, draft => {
    switch (action.type) {
      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
      case WorkspaceActionTypes.RESET_CURRENT_WORKSPACE:
        resetProjects(draft)
        break

      case ProjectActionTypes.FETCH_PROJECTS:
        fetchProjects(draft)
        break

      case ProjectActionTypes.FETCH_PROJECTS_SUCCESS:
        fetchProjectsSuccess(draft, action)
        break

      case ProjectActionTypes.FETCH_PROJECTS_FAILURE:
        fetchProjectsFailure(draft)
        break

      case ProjectActionTypes.UPDATE_PROJECTS_SUCCESS:
        updateProjectsSuccess(draft, action)
        break

      case ProjectActionTypes.CHANGE_SELECTED_PROJECTS:
        changeSelectedProjects(draft, action)
        break

      case ProjectActionTypes.UNSELECT_PROJECTS:
        unselectProjects(draft)
        break

      case LabelActionTypes.CHANGE_SELECTED_LABELS:
      case ProjectVersionSettingsActionTypes.SUBMIT_PROJECT_VERSION_UPDATE:
      case LabelActionTypes.SET_LABEL_SELECTION:
      case ProjectActionTypes.CREATE_PROJECT:
        resetProjectSelection(draft)
        break

      case ProjectActionTypes.DELETE_PROJECTS:
      case ProjectVersionActionTypes.DELETE_PROJECT_VERSIONS:
        deleteProjects(draft)
        break

      case ProjectActionTypes.DELETE_PROJECTS_SUCCESS:
        deleteProjectsSuccess(draft, action)
        break

      case ProjectActionTypes.DELETE_PROJECTS_FAILURE:
      case ProjectVersionActionTypes.DELETE_PROJECT_VERSIONS_FAILURE:
        deleteProjectsFailure(draft)
        break

      case ProjectActionTypes.UPDATE_PROJECT_TITLE:
      case ProjectVersionActionTypes.UPDATE_PROJECT_VERSION_TITLE:
        updateTitle(draft)
        break

      case ProjectActionTypes.UPDATE_PROJECT_TITLE_SUCCESS:
        updateProjectTitleSuccess(draft, action)
        break

      case ProjectActionTypes.UPDATE_PROJECT_TITLE_FAILURE:
      case ProjectVersionActionTypes.UPDATE_PROJECT_VERSION_TITLE_FAILURE:
        updateTitleFailure(draft)
        break

      case ProjectActionTypes.RESET_SHOULD_CHECK_AUTO_FILL_GRID:
        resetShouldCheckAutoFillGrid(draft)
        break

      case ProjectActionTypes.RESET_SHOULD_CHECK_AUTO_FILL_LIST:
        resetShouldCheckAutoFillList(draft)
        break

      case ProjectActionTypes.SET_SHOULD_CHECK_AUTO_FILL:
        setShouldCheckAutoFill(draft)
        break

      case ProjectActionTypes.SET_LIST_ACTIVE:
        setListActive(draft)
        break

      case ProjectActionTypes.SET_GRID_ACTIVE:
        setGridActive(draft)
        break

      case ProjectActionTypes.SET_SEARCH_TEXT:
        setSearchText(draft, action)
        break

      case ProjectActionTypes.START_BULK_EXPORTATION:
        startBulkExportation(draft)
        break

      case ProjectActionTypes.START_BULK_EXPORTATION_SUCCESS:
      case ProjectActionTypes.START_BULK_EXPORTATION_FAILURE:
        resetStartingBulkExportation(draft)
        break

      case SubtitleActionTypes.DELETE_LANGUAGE_SUCCESS:
        deleteLanguageSuccess(draft, action)
        break

      case ProjectVersionActionTypes.DELETE_PROJECT_VERSIONS_SUCCESS:
        deleteProjectVersionsSuccess(draft, action)
        break

      case ProjectVersionActionTypes.UPDATE_PROJECT_VERSION_TITLE_SUCCESS:
        updateProjectVersionTitleSuccess(draft, action)
        break

      default:
        break
    }
  })
}

const resetProjects = (draft: Draft<IProjectState>) => {
  draft.hasLoadedInitially = false
  draft.projects = []
}

const fetchProjects = (draft: Draft<IProjectState>) => {
  draft.loading = true
}

function isIFetchProjectsSuccessAction(arg: any): arg is IAction<IFetchProjectsSuccessResponseBody> {
  return arg.type === ProjectActionTypes.FETCH_PROJECTS_SUCCESS
}

function isProjectVersionData(resource: IncludedResource): resource is IProjectVersionData {
  return ['projectVersion', 'projectVersionDecorator'].includes(resource.type)
}

function isSubtitleData(resource: IncludedResource): resource is ISubtitleData {
  return ['subtitle', 'subtitleDecorator'].includes(resource.type)
}

const buildProjectsFromPayload = (
  data: IProjectData[],
  included: IncludedResource[]
): IProject[] => {
  const projectVersionsData = filter(included, isProjectVersionData)
  const subtitlesData = filter(included, isSubtitleData)

  const projectVersionById = new Map(
    map(projectVersionsData, pv => [pv.id, pv])
  )

  const subtitleById = new Map(
    map(subtitlesData, sub => [sub.id, sub])
  )

  return map(data, project => {
    const projectVersions: IProjectVersion[] =
      map(
        project.relationships.projectVersions.data,
        pvRef => projectVersionById.get(pvRef.id)
      )
        .filter((pv): pv is IProjectVersionData => Boolean(pv))
        .map(pv => {
          const subtitles: ISubtitle[] =
            map(
              pv.relationships.subtitles.data,
              subRef => subtitleById.get(subRef.id)
            )
              .filter((sub): sub is ISubtitleData => Boolean(sub))
              .map(sub => ({
                id: Number(sub.id),
                attributes: sub.attributes,
              }))

          return {
            id: pv.id,
            attributes: pv.attributes,
            subtitles,
          }
        })

    return {
      id: project.id,
      attributes: project.attributes,
      projectVersions,
    }
  })
}

const fetchProjectsSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIFetchProjectsSuccessAction(action)) { return }

  const {
    data,
    included = [],
    totalCount,
    totalPages,
    currentPage,
  } = action.payload

  const loadedProjects = buildProjectsFromPayload(data, included)

  if (
    currentPage === 1 ||
    currentPage === draft.page.currentPage + 1
  ) {
    if (currentPage === 1) {
      draft.projects = loadedProjects
      draft.page.shouldCheckAutoFillGrid = true
      draft.page.shouldCheckAutoFillList = true
    } else {
      draft.projects.push(...loadedProjects)
    }

    draft.page.currentPage = currentPage
  }

  draft.page.totalCount = totalCount
  draft.page.totalPages = totalPages

  draft.hasLoadedInitially = true
  draft.loading = false
}

const fetchProjectsFailure = (draft: Draft<IProjectState>) => {
  draft.loading = false
}

function isIUpdateProjectsSuccessAction(arg: any): arg is IAction<IUpdateProjectsSuccessResponseBody> {
  return arg.type === ProjectActionTypes.UPDATE_PROJECTS_SUCCESS
}

const updateProjectsSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIUpdateProjectsSuccessAction(action)) { return }

  const updatedProjectVersionsData = action.payload.projectVersions.data
  const updatedSubtitlesData = action.payload.subtitles.data

  const projectVersionById = new Map(
    map(updatedProjectVersionsData, pv => [pv.id, pv])
  )

  const subtitleById = new Map(
    map(updatedSubtitlesData, sub => [sub.id, sub])
  )

  draft.projects = map(draft.projects, project => ({
    ...project,
    projectVersions: map(project.projectVersions, projectVersion => {
      const updatedProjectVersionData =
        projectVersionById.get(projectVersion.id)

      const updatedSubtitles = map(projectVersion.subtitles, subtitle => {
        const updatedSubtitleData =
          subtitleById.get(String(subtitle.id))

        return updatedSubtitleData
          ? { ...subtitle, attributes: updatedSubtitleData.attributes }
          : subtitle
      })

      return updatedProjectVersionData
        ? {
            ...projectVersion,
            attributes: updatedProjectVersionData.attributes,
            subtitles: updatedSubtitles,
          }
        : {
            ...projectVersion,
            subtitles: updatedSubtitles,
          }
    }),
  }))
}

function isIChangeSelectedProjectsAction(arg: any): arg is IAction<IChangeSelectedProjectsPayload> {
  return arg.type === ProjectActionTypes.CHANGE_SELECTED_PROJECTS
}

const changeSelectedProjects = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIChangeSelectedProjectsAction(action)) { return }

  const { selectProject, projectId } = action.payload

  if (selectProject) {
    draft.selectedProjectIds.push(projectId)
  } else {
    draft.selectedProjectIds = without(draft.selectedProjectIds, projectId)
  }
}

const unselectProjects = (draft: Draft<IProjectState>) => {
  draft.selectedProjectIds = []
}

const resetProjectSelection = (draft: Draft<IProjectState>) => {
  draft.selectedProjectIds = []
}

const deleteProjects = (draft: Draft<IProjectState>) => {
  draft.deleting = true
}

function isIDeleteProjectsSuccessAction(arg: any): arg is IAction<IDeleteProjectsSuccessPayload> {
  return arg.type === ProjectActionTypes.DELETE_PROJECTS_SUCCESS
}

const deleteProjectsSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIDeleteProjectsSuccessAction(action)) { return }

  draft.projects = filter(draft.projects, project => !action.payload.projectIds.includes(project.id))
  draft.deleting = false
  draft.selectedProjectIds = []
}

const deleteProjectsFailure = (draft: Draft<IProjectState>) => {
  draft.deleting = false
}

const updateTitle = (draft: Draft<IProjectState>) => {
  draft.updating = true
}

function isIUpdateProjectTitleSuccessAction(arg: any): arg is IAction<IUpdateProjectTitleSuccessPayload> {
  return arg.type === ProjectActionTypes.UPDATE_PROJECT_TITLE_SUCCESS
}

const updateProjectTitleSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIUpdateProjectTitleSuccessAction(action)) { return }

  const { projectId, updatedTitle } = action.payload
  const project = find(draft.projects, p => p.id === projectId)

  if (project) {
    project.attributes.title = updatedTitle
  }

  draft.updating = false
  draft.selectedProjectIds = []
}

const updateTitleFailure = (draft: Draft<IProjectState>) => {
  draft.updating = false
}

const resetShouldCheckAutoFillGrid = (draft: Draft<IProjectState>) => {
  draft.page.shouldCheckAutoFillGrid = false
}

const resetShouldCheckAutoFillList = (draft: Draft<IProjectState>) => {
  draft.page.shouldCheckAutoFillList = false
}

const setShouldCheckAutoFill = (draft: Draft<IProjectState>) => {
  draft.page.shouldCheckAutoFillGrid = true
  draft.page.shouldCheckAutoFillList = true
}

const setListActive = (draft: Draft<IProjectState>) => {
  draft.page.listActive = true
}

const setGridActive = (draft: Draft<IProjectState>) => {
  draft.page.listActive = false
}

function isISetSearchTextAction(arg: any): arg is IAction<ISetSearchTextPayload> {
  return arg.type === ProjectActionTypes.SET_SEARCH_TEXT
}

const setSearchText = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isISetSearchTextAction(action)) { return }

  draft.searchText = action.payload.newSearchText
}

const startBulkExportation = (draft: Draft<IProjectState>) => {
  draft.startingBulkExportation = true
}

const resetStartingBulkExportation = (draft: Draft<IProjectState>) => {
  draft.startingBulkExportation = false
}

function isIDeleteLanguageSuccessAction(arg: any): arg is IAction<IDeleteLanguageSuccessPayload> {
  return arg.type === SubtitleActionTypes.DELETE_LANGUAGE_SUCCESS
}

const deleteLanguageSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIDeleteLanguageSuccessAction(action)) { return }

  const { projectVersionId, subtitleId } = action.payload

  for (const project of draft.projects) {
    const projectVersion = project.projectVersions.find(
      pv => pv.id === projectVersionId
    )

    if (!projectVersion) { continue }

    projectVersion.subtitles = projectVersion.subtitles.filter(
      subtitle => subtitle.id !== subtitleId
    )

    break
  }
}

function isIDeleteProjectVersionsSuccessAction(arg: any): arg is IAction<IDeleteProjectVersionsSuccessPayload> {
  return arg.type === ProjectVersionActionTypes.DELETE_PROJECT_VERSIONS_SUCCESS
}

const deleteProjectVersionsSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIDeleteProjectVersionsSuccessAction(action)) { return }

  draft.projects = map(draft.projects, project => {
    return ({
      ...project,
      projectVersions: filter(project.projectVersions, pv => !action.payload.projectVersionIds.includes(pv.id))
    })
  })

  draft.deleting = false
}

function isIUpdateProjectVersionTitleSuccessAction(arg: any): arg is IAction<IUpdateProjectVersionTitleSuccessPayload> {
  return arg.type === ProjectVersionActionTypes.UPDATE_PROJECT_VERSION_TITLE_SUCCESS
}

const updateProjectVersionTitleSuccess = (draft: Draft<IProjectState>, action: TAction) => {
  if (!isIUpdateProjectVersionTitleSuccessAction(action)) { return }

  const { projectVersionId, updatedTitle } = action.payload

  for (const project of draft.projects) {
    const projectVersion = find(project.projectVersions, pv => pv.id === projectVersionId)

    if (projectVersion) {
      projectVersion.attributes.title = updatedTitle
      break
    }
  }

  draft.updating = false
  draft.selectedProjectIds = []
}
